import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateTemplate, TokenStandard, ParameterSchema } from '../../../hooks/useSmartContractTemplates';
import { SolidityCodeViewer } from '../../../components/smart-contracts';
import { PageHeader } from '../../../components/help/PageHeader';
import { Button } from '../../../components/ui/Button';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  FileCode,
  AlertCircle
} from 'lucide-react';

const DEFAULT_ERC20_CODE = `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.20;

import "@openzeppelin/contracts/token/ERC20/ERC20.sol";
import "@openzeppelin/contracts/access/Ownable.sol";

contract {{name}} is ERC20, Ownable {
    constructor(
        string memory name_,
        string memory symbol_,
        uint256 initialSupply_
    ) ERC20(name_, symbol_) Ownable(msg.sender) {
        _mint(msg.sender, initialSupply_ * 10 ** decimals());
    }

    function mint(address to, uint256 amount) public onlyOwner {
        _mint(to, amount);
    }

    function burn(uint256 amount) public {
        _burn(msg.sender, amount);
    }
}`;

const DEFAULT_PARAMS: ParameterSchema[] = [
  { name: 'name', type: 'string', required: true, description: 'Nome do token' },
  { name: 'symbol', type: 'string', required: true, description: 'Símbolo do token (ex: BTC)' },
  { name: 'initialSupply', type: 'uint256', required: true, description: 'Supply inicial do token' },
];

export function SmartContractTemplateCreate() {
  const navigate = useNavigate();
  const createMutation = useCreateTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tokenStandard, setTokenStandard] = useState<TokenStandard>('ERC20');
  const [sourceCode, setSourceCode] = useState(DEFAULT_ERC20_CODE);
  const [documentationUrl, setDocumentationUrl] = useState('');
  const [parameterSchema, setParameterSchema] = useState<ParameterSchema[]>(DEFAULT_PARAMS);
  const [errors, setErrors] = useState<string[]>([]);

  const handleAddParameter = () => {
    setParameterSchema([
      ...parameterSchema,
      { name: '', type: 'string', required: false, description: '' },
    ]);
  };

  const handleRemoveParameter = (index: number) => {
    setParameterSchema(parameterSchema.filter((_, i) => i !== index));
  };

  const handleUpdateParameter = (index: number, field: keyof ParameterSchema, value: any) => {
    const updated = [...parameterSchema];
    updated[index] = { ...updated[index], [field]: value };
    setParameterSchema(updated);
  };

  const validate = (): boolean => {
    const newErrors: string[] = [];
    
    if (!name.trim()) {
      newErrors.push('Nome é obrigatório');
    }
    if (!sourceCode.trim()) {
      newErrors.push('Código fonte é obrigatório');
    }
    if (parameterSchema.length === 0) {
      newErrors.push('Pelo menos um parâmetro é obrigatório');
    }
    parameterSchema.forEach((param, index) => {
      if (!param.name.trim()) {
        newErrors.push(`Parâmetro ${index + 1}: nome é obrigatório`);
      }
      if (!param.description.trim()) {
        newErrors.push(`Parâmetro ${index + 1}: descrição é obrigatória`);
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const template = await createMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        tokenStandard,
        sourceCode,
        documentationUrl: documentationUrl.trim() || undefined,
        parameterSchema,
      });
      navigate(`/admin/tokenization/contracts/${template.id}`);
    } catch (error) {
      setErrors(['Erro ao criar template. Tente novamente.']);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/tokenization/contracts')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-surface-100">Novo Template</h1>
            <p className="text-surface-400">Crie um novo template de smart contract</p>
          </div>
        </div>
        <Button onClick={handleSubmit} disabled={createMutation.isPending}>
          <Save className="w-4 h-4 mr-2" />
          {createMutation.isPending ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-400 mb-2">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Corrija os erros abaixo:</span>
          </div>
          <ul className="list-disc list-inside text-red-300 text-sm space-y-1">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-1 space-y-4">
          {/* Basic Info */}
          <div className="bg-surface-800 rounded-lg border border-surface-700 p-4 space-y-4">
            <h2 className="font-medium text-surface-100">Informações Básicas</h2>
            
            <div>
              <label className="block text-sm text-surface-400 mb-1">Nome *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Token ERC-20 Padrão"
                className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm text-surface-400 mb-1">Descrição</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva o propósito deste template..."
                rows={3}
                className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm text-surface-400 mb-1">Padrão do Token *</label>
              <select
                value={tokenStandard}
                onChange={(e) => setTokenStandard(e.target.value as TokenStandard)}
                className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="ERC20">ERC-20 (Fungível)</option>
                <option value="ERC1400">ERC-1400 (Security Token)</option>
                <option value="ERC4626">ERC-4626 (Vault)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-surface-400 mb-1">URL da Documentação</label>
              <input
                type="url"
                value={documentationUrl}
                onChange={(e) => setDocumentationUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Parameters */}
          <div className="bg-surface-800 rounded-lg border border-surface-700 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-medium text-surface-100">Parâmetros</h2>
              <Button variant="ghost" size="sm" onClick={handleAddParameter}>
                <Plus className="w-4 h-4 mr-1" />
                Adicionar
              </Button>
            </div>

            <p className="text-sm text-surface-400">
              Defina os parâmetros que serão substituídos no código usando a sintaxe {'{{nome}}'}.
            </p>

            <div className="space-y-3">
              {parameterSchema.map((param, index) => (
                <div key={index} className="bg-surface-900 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-surface-400">Parâmetro {index + 1}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-1 text-red-400 hover:text-red-300"
                      onClick={() => handleRemoveParameter(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={param.name}
                      onChange={(e) => handleUpdateParameter(index, 'name', e.target.value)}
                      placeholder="Nome"
                      className="px-2 py-1 bg-surface-800 border border-surface-700 rounded text-sm text-surface-100 placeholder-surface-500"
                    />
                    <select
                      value={param.type}
                      onChange={(e) => handleUpdateParameter(index, 'type', e.target.value)}
                      className="px-2 py-1 bg-surface-800 border border-surface-700 rounded text-sm text-surface-100"
                    >
                      <option value="string">string</option>
                      <option value="uint256">uint256</option>
                      <option value="address">address</option>
                      <option value="bool">bool</option>
                      <option value="bytes32">bytes32</option>
                    </select>
                  </div>
                  
                  <input
                    type="text"
                    value={param.description}
                    onChange={(e) => handleUpdateParameter(index, 'description', e.target.value)}
                    placeholder="Descrição"
                    className="w-full px-2 py-1 bg-surface-800 border border-surface-700 rounded text-sm text-surface-100 placeholder-surface-500"
                  />
                  
                  <label className="flex items-center gap-2 text-sm text-surface-300">
                    <input
                      type="checkbox"
                      checked={param.required}
                      onChange={(e) => handleUpdateParameter(index, 'required', e.target.checked)}
                      className="rounded border-surface-600"
                    />
                    Obrigatório
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Code Editor */}
        <div className="lg:col-span-2">
          <div className="bg-surface-800 rounded-lg border border-surface-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-surface-700 flex items-center gap-2">
              <FileCode className="w-5 h-5 text-primary-400" />
              <h2 className="font-medium text-surface-100">Código Solidity</h2>
            </div>
            <SolidityCodeViewer
              code={sourceCode}
              readOnly={false}
              onCodeChange={setSourceCode}
              height="600px"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default SmartContractTemplateCreate;
